import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import LegalDocumentLanguageToggle from '../components/LegalDocumentLanguageToggle';
import { privacyPolicyJsonUrl } from '../config/privacyPolicy';
import {
    type DocumentLocale,
    persistDocumentLocale,
    resolveDocumentLocale,
} from '../utils/documentLocale';
import { getLegalDocumentChrome } from '../utils/legalDocumentChrome';
import {
    fetchLegalDocument,
    type LegalDocument,
} from '../utils/legalDocumentTypes';
import { renderPolicyBlock } from '../utils/renderPolicyBlock';
import './PrivacyPolicyPage.css';

const PrivacyPolicyPage: React.FC = () => {
    const { version } = useParams<{ version: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const [locale, setLocale] = useState<DocumentLocale>(() =>
        resolveDocumentLocale(searchParams)
    );
    const [policy, setPolicy] = useState<LegalDocument | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const chrome = getLegalDocumentChrome(locale);

    const handleLocaleChange = useCallback(
        (nextLocale: DocumentLocale) => {
            persistDocumentLocale(nextLocale);
            setLocale(nextLocale);

            const nextParams = new URLSearchParams(searchParams);
            nextParams.set('lang', nextLocale);
            setSearchParams(nextParams, { replace: true });
        },
        [searchParams, setSearchParams]
    );

    useEffect(() => {
        const resolvedLocale = resolveDocumentLocale(searchParams);
        setLocale(resolvedLocale);
    }, [searchParams]);

    useEffect(() => {
        if (!version) {
            setError(chrome.versionNotSpecified);
            setIsLoading(false);
            return;
        }

        let isCancelled = false;

        const loadPolicy = async () => {
            setIsLoading(true);
            setError(null);
            setPolicy(null);

            try {
                const localizedUrl = privacyPolicyJsonUrl(version, locale);
                const fallbackUrl = privacyPolicyJsonUrl(version, 'tr');
                const data = await fetchLegalDocument<LegalDocument>(
                    localizedUrl,
                    fallbackUrl
                );

                if (!isCancelled) {
                    setPolicy(data);
                }
            } catch {
                if (!isCancelled) {
                    setError(chrome.privacyPolicyLoadFailed);
                }
            } finally {
                if (!isCancelled) {
                    setIsLoading(false);
                }
            }
        };

        loadPolicy();

        return () => {
            isCancelled = true;
        };
    }, [version, locale, chrome.privacyPolicyLoadFailed, chrome.versionNotSpecified]);

    if (isLoading) {
        return (
            <div className="privacy-policy-page">
                <div className="container">
                    <div className="page-header">
                        <h1>{chrome.privacyPolicyTitle}</h1>
                        <p className="last-updated">{chrome.loading}</p>
                        <LegalDocumentLanguageToggle
                            locale={locale}
                            onChange={handleLocaleChange}
                        />
                    </div>
                </div>
            </div>
        );
    }

    if (error || !policy) {
        return (
            <div className="privacy-policy-page">
                <div className="container">
                    <div className="page-header">
                        <h1>{chrome.privacyPolicyTitle}</h1>
                        <p className="last-updated">
                            {error ?? chrome.privacyPolicyLoadFailed}
                        </p>
                        <LegalDocumentLanguageToggle
                            locale={locale}
                            onChange={handleLocaleChange}
                        />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="privacy-policy-page">
            <div className="container">
                <div className="page-header">
                    <h1>{policy.title}</h1>
                    <p className="last-updated">
                        {chrome.lastUpdated} {policy.lastUpdated}
                    </p>
                    <LegalDocumentLanguageToggle
                        locale={locale}
                        onChange={handleLocaleChange}
                    />
                </div>

                <div className="policy-content">
                    {policy.sections.map((section, sectionIndex) => (
                        <section key={sectionIndex} className="policy-section">
                            <h2>{section.title}</h2>
                            {section.blocks.map((block, blockIndex) =>
                                renderPolicyBlock(block, blockIndex)
                            )}
                        </section>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default PrivacyPolicyPage;
